import {
    LayoutDashboard,
    CalendarCheck,
    CreditCard,
    Settings,
    ShoppingBag,
    MapPin,
    LogOut,
    PanelLeft,
    PanelLeftClose,
    X,
    Users,
    UserPlus,
    UserCheck,
    Gamepad2,
    Star,
    Crown,
    Trophy,
    ChevronDown,
    LucideIcon
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';

import logo from '../../admin/pages/assets/logo.svg';
import { useAccessRules } from '../../shared/hooks/useAccessRules';
import { unwrapApiArray } from '../../shared/utils/apiResponse';
import { useSidebar } from '../app/providers';
import { useUserContext } from '../contexts/userContext';
import { handleOrganizerFetchGames } from '../userControllers/organizerGameController';
import { handleOrganizerFetchPitches } from '../userControllers/organizerPitchController';


interface NavItem {
    id: string;
    label: string;
    path: string;
    icon: LucideIcon;
    exact?: boolean;
    badgeCount?: number | undefined;
}

const OVERVIEW_NAV_ITEM: NavItem = {
    id: 'overview',
    label: 'Overview',
    path: '/dashboard',
    icon: LayoutDashboard,
    exact: true,
};

const MATCHES_NAV_ITEM: NavItem = {
    id: 'matches',
    label: 'Matches',
    path: '/dashboard/matches',
    icon: CalendarCheck,
};

// Organizer-only menu items (approved organizers)
const APPROVED_ORGANIZER_NAV_ITEMS: NavItem[] = [
    { id: 'organizer-profile', label: 'Organizer Profile', path: '/dashboard/organizer', icon: UserCheck },
    { id: 'my-venues', label: 'My Venues', path: '/dashboard/my-venues', icon: MapPin },
    { id: 'my-games', label: 'Games', path: '/dashboard/my-games', icon: Gamepad2 },
    { id: 'my-payout', label: 'Payout History', path: '/dashboard/payouts', icon: CreditCard },
    { id: 'payout-settings', label: 'Payout Settings', path: '/dashboard/payout-settings', icon: Settings },
    { id: 'reviews', label: 'Reviews', path: '/dashboard/reviews', icon: Star },
];

// Non-organizer menu item
const BECOME_ORGANIZER_NAV_ITEM: NavItem = {
    id: 'become-organizer',
    label: 'Become an Organizer',
    path: '/dashboard/organizer',
    icon: UserPlus
};

// SACA Pro - visible to all users
const SACA_PRO_NAV_ITEM: NavItem = {
    id: 'saca-pro',
    label: 'SACA Pro',
    path: '/dashboard/pro',
    icon: Crown
};

// Always visible at bottom
const REFERRALS_NAV_ITEM: NavItem = {
    id: 'referrals',
    label: 'Referrals',
    path: '/dashboard/referrals',
    icon: Users
};

const COMPETITIONS_NAV_ITEM: NavItem = {
    id: 'competitions',
    label: 'Competitions',
    path: '/dashboard/competitions',
    icon: Trophy,
};

const GENERAL_NAV_ITEMS: NavItem[] = [
    { id: 'venues', label: 'Pitches & Venues', path: '/dashboard/venues', icon: MapPin },
    COMPETITIONS_NAV_ITEM,
    { id: 'transactions', label: 'Transactions', path: '/dashboard/wallet', icon: CreditCard },
    { id: 'store', label: 'Store', path: '/dashboard/shop', icon: ShoppingBag },
    { id: 'settings', label: 'Settings', path: '/dashboard/settings', icon: Settings },
    SACA_PRO_NAV_ITEM,
    REFERRALS_NAV_ITEM,
];

const Sidebar: React.FC = () => {
    const { isCollapsed, toggleSidebar, isMobileOpen, setIsMobileOpen } = useSidebar();
    const { userProfile, saveUserProfile } = useUserContext();
    const [isOrganizerSectionExpanded, setIsOrganizerSectionExpanded] = useState(true);
    const [pendingCounts, setPendingCounts] = useState({ games: 0, pitches: 0 });

    // Determine if user is an approved organizer
    const access = useAccessRules({ organizerProfile: userProfile });
    const isOrganizer = useMemo(() => access.isOrganizerFull, [access.isOrganizerFull]);

    useEffect(() => {
        if (!isOrganizer) {
            setPendingCounts({ games: 0, pitches: 0 });
            return;
        }

        let isActive = true;

        const fetchPendingCounts = async () => {
            try {
                const [gamesResponse, pitchesResponse] = await Promise.all([
                    handleOrganizerFetchGames<Record<string, unknown>[]>(),
                    handleOrganizerFetchPitches(),
                ]);

                const games = unwrapApiArray<Record<string, unknown>>(gamesResponse);
                const pitches = unwrapApiArray<Record<string, unknown>>(pitchesResponse);

                const gamePendingCount = games.filter((game) => {
                    const status = String(game.host_status ?? game.status ?? '').trim().toLowerCase();
                    return status === 'pending' || status === 'submitted';
                }).length;

                const pitchPendingCount = pitches.filter((pitch) => {
                    const status = String(pitch.status ?? '').trim().toLowerCase();
                    return status === 'pending' || status === 'submitted';
                }).length;

                if (isActive) {
                    setPendingCounts({
                        games: gamePendingCount,
                        pitches: pitchPendingCount,
                    });
                }
            } catch {
                if (isActive) {
                    setPendingCounts({ games: 0, pitches: 0 });
                }
            }
        };

        void fetchPendingCounts();
        const intervalId = globalThis.setInterval(fetchPendingCounts, 60_000);

        return () => {
            isActive = false;
            globalThis.clearInterval(intervalId);
        };
    }, [isOrganizer]);

    // Build navigation items based on organizer status
    const organizerNavItems = useMemo(
        () => {
            const items = isOrganizer ? APPROVED_ORGANIZER_NAV_ITEMS : [BECOME_ORGANIZER_NAV_ITEM];
            return items.map((item) => {
                if (item.id === 'my-venues') {
                    return {
                        ...item,
                        badgeCount: pendingCounts.pitches > 0 ? pendingCounts.pitches : undefined,
                    };
                }

                if (item.id === 'my-games') {
                    return {
                        ...item,
                        badgeCount: pendingCounts.games > 0 ? pendingCounts.games : undefined,
                    };
                }

                return item;
            });
        },
        [isOrganizer, pendingCounts.games, pendingCounts.pitches]
    );

    const handleLogout = () => {
        saveUserProfile(null);
        globalThis.localStorage?.removeItem('saca_token');
        globalThis.location.href = '/sign-in';
    };

    const renderNavItem = (item: NavItem, isNested = false) => (
        <NavLink
            key={item.id}
            to={item.path}
            end={Boolean(item.exact)}
            onClick={() => setIsMobileOpen(false)}
            className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                ${isNested ? 'ml-4' : ''}
                ${isActive
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}
                ${isCollapsed && !isNested ? 'justify-center px-2' : ''}
            `}
            title={isCollapsed ? item.label : undefined}
        >
            <item.icon size={isNested ? 16 : 18} className="flex-shrink-0" />
            {(!isCollapsed || isNested) && (
                <span className="font-medium text-sm">{item.label}</span>
            )}
            {!isCollapsed && typeof item.badgeCount === 'number' && item.badgeCount > 0 && (
                <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-100 px-1.5 text-xs font-semibold text-amber-800">
                    {item.badgeCount}
                </span>
            )}
        </NavLink>
    );

    return (
        <>
            {/* Mobile Overlay */}
            {isMobileOpen && (
                <button
                    type="button"
                    aria-label="Close sidebar menu"
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setIsMobileOpen(false)}
                />
            )}

            {/* Sidebar Container */}
            <aside
                className={`
                    fixed top-0 left-0 z-40 h-screen bg-white border-r border-slate-200 transition-all duration-300
                    ${isCollapsed ? 'w-20' : 'w-64'}
                    ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                `}
            >
                {/* Logo Area */}
                <div className={`flex items-center h-16 px-4 border-b border-slate-100 ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
                    {!isCollapsed && (
                        <div className="flex items-center">
                            <img src={logo} alt="SACA" className="h-8" />
                        </div>
                    )}

                    {/* Collapsed state logo - simplified or icon */}
                    {isCollapsed && (
                        <div className="flex items-center justify-center">
                            <span className="text-xl font-bold text-primary">S</span>
                        </div>
                    )}

                    {/* Desktop collapse toggle */}
                    <button
                        onClick={toggleSidebar}
                        className="hidden lg:flex p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {isCollapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
                    </button>

                    {/* Mobile Close Button */}
                    <button
                        onClick={() => setIsMobileOpen(false)}
                        className="lg:hidden text-slate-400 hover:text-slate-600"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Navigation */}
                <div className="flex flex-col h-[calc(100vh-64px)] justify-between py-4">
                    <nav className="space-y-3 px-2">
                        <div className="space-y-1">{renderNavItem(OVERVIEW_NAV_ITEM)}</div>

                        <div className="space-y-1">
                            {isCollapsed ? (
                                <div className="relative group/tooltip">
                                    <button
                                        type="button"
                                        onClick={() => setIsOrganizerSectionExpanded((prev) => !prev)}
                                        className="flex items-center justify-center w-full p-2.5 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                                    >
                                        <UserCheck size={18} />
                                    </button>
                                    <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover/tooltip:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                                        Organizer Management
                                    </div>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setIsOrganizerSectionExpanded((prev) => !prev)}
                                    className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <UserCheck size={18} className="flex-shrink-0" />
                                        <span className="font-medium text-sm">Organizer Management</span>
                                    </div>
                                    <ChevronDown
                                        size={16}
                                        className={`transition-transform duration-200 ${isOrganizerSectionExpanded ? 'rotate-180' : ''}`}
                                    />
                                </button>
                            )}

                            <div
                                className={`overflow-hidden transition-all duration-200 ${!isCollapsed && isOrganizerSectionExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                                    }`}
                            >
                                <div className="py-1 space-y-0.5">
                                    {organizerNavItems.map((item) => renderNavItem(item, true))}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-1">{renderNavItem(MATCHES_NAV_ITEM)}</div>

                        <div className="space-y-1">
                            {GENERAL_NAV_ITEMS.map((item) => renderNavItem(item))}
                        </div>
                    </nav>

                    {/* Footer Actions */}
                    <div className="px-2 space-y-1">
                        <button
                            onClick={handleLogout}
                            className={`
                                w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors
                                ${isCollapsed ? 'justify-center' : ''}
                            `}
                            title="Logout"
                        >
                            <LogOut size={20} className="flex-shrink-0" />
                            {!isCollapsed && <span>Sign Out</span>}
                        </button>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
